import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { API_BASE, LiveBoardRow } from '../org/scoring-types';
import {
  displayStatusLabel,
  PRE_RACE_SENTINEL,
  SELECTABLE_STATUSES,
  STATUS_COLOURS,
} from '../org/StatusChip';
import useWebSocketHandler from '../wsHookHandler';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface AllCarsWsMessage {
  CarNumber: number;
  row: LiveBoardRow | null;
}

interface UidWsMessage {
  CarNumber: number;
  UID: string;
}

interface UidOption {
  UID: string;
  Name?: string;
}

function getErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e) && e.response?.data) {
    if (typeof e.response.data === 'string') return e.response.data;
    return (e.response.data as { error?: string }).error ?? JSON.stringify(e.response.data);
  }
  return (e as Error).message;
}

// Map any DB/wire representation of "no status yet" back onto the
// PRE_RACE_SENTINEL the <Select> controls. Keeps the status dropdown
// valued even when the car row still has the schema default '"---"'.
function normalizeStatus(status: string | null | undefined): string {
  if (!status) return PRE_RACE_SENTINEL;
  const trimmed = status.trim();
  if (!trimmed || trimmed === '"---"' || trimmed === '---') return PRE_RACE_SENTINEL;
  return trimmed;
}

// Shared chip renderer used by both the <Select>'s MenuItems and its
// renderValue, so the field displays the exact same pill that the
// LiveBoard's StatusChip uses.
function StatusPill({ value, sizeMinWidth }: { value: string; sizeMinWidth?: number }) {
  const label = displayStatusLabel(value);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: STATUS_COLOURS[value] ?? 'slategrey',
        color: 'white',
        fontWeight: 500,
        minWidth: sizeMinWidth,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
// Tab indices. "Submit" is the default so the operator lands on the
// assignment controls; "Roster" is a read-only overview.
const TAB_SUBMIT = 0;
const TAB_ROSTER = 1;
const DEFAULT_TAB = TAB_SUBMIT;

const OrgLocation: React.FC = () => {
  const [tab, setTab] = useState<number>(DEFAULT_TAB);
  const [rows, setRows] = useState<Record<number, LiveBoardRow>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedCarNumber, setSelectedCarNumber] = useState<number | ''>('');
  const [statusDraft, setStatusDraft] = useState<string>(PRE_RACE_SENTINEL);
  const [uidDraft, setUidDraft] = useState<string>('');

  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingUid, setSavingUid] = useState(false);

  // Initial snapshot. The same /allCarsBoard endpoint the LiveBoard uses;
  // UID lives on the row now so we don't need a second fetch for it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get<LiveBoardRow[]>(
          `${API_BASE}/api/scoring/allCarsBoard`,
        );
        if (cancelled) return;
        const next: Record<number, LiveBoardRow> = {};
        for (const row of data) next[row.CarNumber] = row;
        setRows(next);
      } catch (e) {
        if (!cancelled) setLoadError(getErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sensor options for the UID dropdown.
  const { data: uidOptions = [] } = useQuery({
    queryKey: ['allUIDs'],
    queryFn: async () => (await axios.get<UidOption[]>(`${API_BASE}/api/allUIDs`)).data,
  });

  // Live updates. 'allCarsBoard' carries every mutation that flows
  // through broadcastLiveBoardRow (status, penalty, transponder pairing,
  // lap builds). 'uid' carries the standalone setUID writes which do not
  // trip that broadcast.
  const onWsMessage = useCallback(
    (msg: { channel: string; message: AllCarsWsMessage | UidWsMessage }) => {
      if (msg.channel === 'allCarsBoard') {
        const payload = msg.message as AllCarsWsMessage;
        if (!payload.row) {
          setRows((prev) => {
            if (!(payload.CarNumber in prev)) return prev;
            const next = { ...prev };
            delete next[payload.CarNumber];
            return next;
          });
          return;
        }
        setRows((prev) => ({ ...prev, [payload.CarNumber]: payload.row as LiveBoardRow }));
        return;
      }

      if (msg.channel === 'uid') {
        const payload = msg.message as UidWsMessage;
        setRows((prev) => {
          const next = { ...prev };
          // setUID does a delete-then-update on the server: clear any
          // other car that previously held this UID so the UI matches.
          for (const raw of Object.keys(next)) {
            const car = Number(raw);
            if (car !== payload.CarNumber && next[car]?.UID === payload.UID) {
              next[car] = { ...next[car], UID: null };
            }
          }
          if (next[payload.CarNumber]) {
            next[payload.CarNumber] = { ...next[payload.CarNumber], UID: payload.UID };
          }
          return next;
        });
      }
    },
    [],
  );

  useWebSocketHandler<AllCarsWsMessage | UidWsMessage>(['allCarsBoard', 'uid'], onWsMessage);

  const sortedRows = useMemo(
    () => Object.values(rows).sort((a, b) => a.CarNumber - b.CarNumber),
    [rows],
  );

  const selectedRow = selectedCarNumber === '' ? null : rows[selectedCarNumber] ?? null;

  // Re-sync the draft fields whenever the selected car changes or its
  // row updates under us over the websocket.
  useEffect(() => {
    if (!selectedRow) return;
    setStatusDraft(normalizeStatus(selectedRow.Status));
    setUidDraft(selectedRow.UID ?? '');
  }, [selectedRow]);

  const handleCarChange = (e: SelectChangeEvent<number | ''>) => {
    setSelectedCarNumber(e.target.value as number | '');
    setActionError(null);
    setActionOk(null);
  };

  const requireCar = (): number | null => {
    if (selectedCarNumber === '') {
      setActionError('Select a car first.');
      setActionOk(null);
      return null;
    }
    return selectedCarNumber;
  };

  const saveStatus = async () => {
    const car = requireCar();
    if (car == null) return;
    setSavingStatus(true);
    setActionError(null);
    setActionOk(null);
    try {
      await axios.post(`${API_BASE}/api/setStatus`, {
        CarNumber: car,
        Status: statusDraft,
      });
      setActionOk(`Car ${car} status → ${displayStatusLabel(statusDraft)}.`);
    } catch (e) {
      setActionError(getErrorMessage(e));
    } finally {
      setSavingStatus(false);
    }
  };

  const saveUid = async () => {
    const car = requireCar();
    if (car == null) return;
    if (!uidDraft) {
      setActionError('Select a UID before submitting.');
      setActionOk(null);
      return;
    }
    setSavingUid(true);
    setActionError(null);
    setActionOk(null);
    try {
      await axios.post(`${API_BASE}/api/setUID`, {
        CarNumber: car,
        UID: uidDraft,
      });
      setActionOk(`Car ${car} sensor → ${uidDraft}.`);
    } catch (e) {
      setActionError(getErrorMessage(e));
    } finally {
      setSavingUid(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Paper
        sx={{
          m: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Submit" />
          <Tab label={`Roster (${sortedRows.length})`} />
        </Tabs>

        {/* --- Assignment controls ------------------------------------ */}
        <Box
          sx={{
            p: { xs: 1, sm: 2 },
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: tab === TAB_SUBMIT ? 'block' : 'none',
          }}
        >
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="location-car-select-label">Car</InputLabel>
              <Select
                labelId="location-car-select-label"
                label="Car"
                value={selectedCarNumber}
                onChange={handleCarChange}
              >
                <MenuItem value="">
                  <em>Select car…</em>
                </MenuItem>
                {sortedRows.map((row) => (
                  <MenuItem key={row.CarNumber} value={row.CarNumber}>
                    {row.CarNumber} — {row.Institution}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel id="location-status-select-label">Status</InputLabel>
                <Select
                  labelId="location-status-select-label"
                  label="Status"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  renderValue={(v) => <StatusPill value={v as string} />}
                >
                  {SELECTABLE_STATUSES.map((option) => (
                    <MenuItem key={option} value={option} sx={{ py: 0.5 }}>
                      <StatusPill value={option} sizeMinWidth={120} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={saveStatus}
                disabled={savingStatus}
                sx={{ minWidth: { xs: '100%', md: 160 } }}
              >
                {savingStatus ? 'Saving…' : 'Submit Status'}
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel id="location-uid-select-label">Sensor UID</InputLabel>
                <Select
                  labelId="location-uid-select-label"
                  label="Sensor UID"
                  value={uidDraft}
                  onChange={(e) => setUidDraft(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select UID…</em>
                  </MenuItem>
                  {uidOptions.map((opt) => (
                    <MenuItem key={opt.UID} value={opt.UID}>
                      {opt.UID}
                      {opt.Name ? ` (${opt.Name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={saveUid}
                disabled={savingUid}
                sx={{ minWidth: { xs: '100%', md: 160 } }}
              >
                {savingUid ? 'Saving…' : 'Submit UID'}
              </Button>
            </Stack>

            {actionError && <Alert severity="error">{actionError}</Alert>}
            {actionOk && <Alert severity="success">{actionOk}</Alert>}
          </Stack>
        </Box>

        {/* --- Read-only roster --------------------------------------- */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: tab === TAB_ROSTER ? 'block' : 'none',
          }}
        >
          {loadError && (
            <Typography color="error" sx={{ p: 2 }}>
              Failed to load initial data: {loadError}
            </Typography>
          )}
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Car', 'Institution', 'Status', 'Sensor (UID)'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                      No cars loaded yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((row) => (
                    <TableRow
                      key={row.CarNumber}
                      hover
                      selected={row.CarNumber === selectedCarNumber}
                      onClick={() => {
                        setSelectedCarNumber(row.CarNumber);
                        setActionError(null);
                        setActionOk(null);
                        // Jump straight to the Submit tab so the operator
                        // can act on the row they just tapped without a
                        // second navigation step.
                        setTab(TAB_SUBMIT);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{row.CarNumber}</TableCell>
                      <TableCell>{row.Institution}</TableCell>
                      <TableCell>
                        <StatusPill value={normalizeStatus(row.Status)} />
                      </TableCell>
                      <TableCell>{row.UID ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default OrgLocation;
